import DOMPurify from 'dompurify';
import { BaseText, Text } from 'slate';

import { CustomElement } from '../../common/TextArea/constants.js';
import {
  CustomSlateNode,
  extendedNodeTypes,
  stylesAttributes,
  type CustomLeaf,
} from '../../common/TextArea/utils.jsx';

const serializeTextNode = (node: CustomSlateNode, type?: string) => {
  const textNode = node as BaseText & CustomLeaf;
  let string = DOMPurify.sanitize(textNode.text);
  // Replace newline characters with <br /> tags to preserve line breaks
  string = string.replaceAll('\n', '<br />');

  // Loop through each style attribute and wrap the text in the corresponding HTML tag
  Object.keys(stylesAttributes).forEach((style) => {
    const isAttribute = textNode[style as keyof CustomLeaf];

    if (isAttribute) {
      const assignedStyle =
        stylesAttributes[style as keyof typeof stylesAttributes];
      string = `<${assignedStyle}>${string}</${assignedStyle}>`;
    }
  });
  // If the type includes 'list', wrap the text in <li> tags
  return type?.includes('list') ? `<li>${string}</li>` : string;
};

const serializeElement = (node: CustomSlateNode) => {
  const element = node as CustomElement;
  const children = element.children
    .map((n) => serializeSlateToHtml(n as CustomSlateNode, node.type))
    .join('');

  // prevent adding empty tags to editor
  if (!children) {
    return '';
  }

  const tag =
    extendedNodeTypes[
      (element as CustomSlateNode).type as keyof typeof extendedNodeTypes
    ];

  return tag ? `<${tag}>${children}</${tag}>` : children;
};

export const serializeSlateToHtml = (
  node: CustomSlateNode,
  type?: string,
): string => {
  if (Text.isText(node)) {
    return serializeTextNode(node, type);
  }

  return serializeElement(node);
};
